import { FC, useMemo, useState } from 'react';
import { Box, FormLabel, MenuItem, Popover, Select, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { TRAVEL_CLASS_OPTIONS, TravelClass } from '../../../../../types/flight.types';

import styles from './TravelersSelect.module.scss';
import { TrevelersIcon } from '../../../../../components/Icons/TrevelersIcon';

type TravelersSelectProps = {
  travelersCount: number;
  travelClass: TravelClass;
  onTravelersCountChange: (n: number) => void;
  onTravelClassChange: (c: TravelClass) => void;
  invalid?: boolean;
  compactForMobile?: boolean;
};

const TravelersSelect: FC<TravelersSelectProps> = ({
  travelersCount,
  travelClass,
  onTravelersCountChange,
  onTravelClassChange,
  invalid = false,
  compactForMobile = false,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const travelClassLabel = useMemo(
    () => TRAVEL_CLASS_OPTIONS.find(o => o.value === travelClass)?.label ?? travelClass,
    [travelClass]
  );

  const summary = useMemo(() => `${travelersCount}, ${travelClassLabel}`, [travelersCount, travelClassLabel]);

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget);
  };

  const close = () => setAnchorEl(null);

  const dec = () => onTravelersCountChange(Math.max(1, travelersCount - 1));
  const inc = () => onTravelersCountChange(Math.min(9, travelersCount + 1));

  return (
    <Box
      className={styles.root}
      data-compact-mobile={compactForMobile ? 'true' : undefined}
    >
      <FormLabel className={styles.label}>
        <Typography variant="h11Semibold" color="#222B3A">Travelers</Typography>
      </FormLabel>

      <Box
        role="button"
        tabIndex={0}
        className={`${styles.trigger}${invalid ? ` ${styles.triggerInvalid}` : ''}`}
        onClick={handleOpen}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setAnchorEl(e.currentTarget);
          }
        }}
        aria-expanded={open}
        aria-haspopup="true"
      >
        <TrevelersIcon className={styles.triggerIcon} />
        <Typography
          variant="h10"
          className={styles.triggerText}
          noWrap
        >
          {summary}
        </Typography>
        <ExpandMoreIcon className={styles.chevron} />
      </Box>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            className: styles.paper,
          },
        }}
        disableRestoreFocus
      >
        <Box className={styles.popoverInner}>
          <Typography
            variant="h11Semibold"
            className={styles.sectionLabel}
          >
            Travel Type
          </Typography>
          <Select
            fullWidth
            value={travelClass}
            onChange={e => onTravelClassChange(e.target.value as TravelClass)}
            displayEmpty
            className={styles.travelTypeSelect}
            IconComponent={ExpandMoreIcon}
            MenuProps={{ className: styles.menuPaper }}
            renderValue={selected =>
              TRAVEL_CLASS_OPTIONS.find(o => o.value === selected)?.label ?? String(selected)
            }
          >
            {TRAVEL_CLASS_OPTIONS.map(opt => (
              <MenuItem
                key={opt.value}
                value={opt.value}
              >
                {opt.label}
              </MenuItem>
            ))}
          </Select>

          <Box className={styles.passengersRow}>
            <Typography
              variant="h10Semibold"
              className={styles.passengersLabel}
            >
              Passengers
            </Typography>
            <Box className={styles.counter}>
              <button
                type="button"
                className={styles.counterBtn}
                onClick={dec}
                disabled={travelersCount <= 1}
                aria-label="Decrease passengers"
              >
                −
              </button>
              <Box className={styles.counterValue}>{travelersCount}</Box>
              <button
                type="button"
                className={styles.counterBtn}
                onClick={inc}
                disabled={travelersCount >= 9}
                aria-label="Increase passengers"
              >
                +
              </button>
            </Box>
          </Box>
        </Box>
      </Popover>
    </Box>
  );
};

export default TravelersSelect;
